import React, { memo, useEffect, useState } from 'react';

import { useDebounce } from 'react-use';

import { Slider, withStyles } from '@material-ui/core';

const PrettoSingleSlider = withStyles({
  root: {
    color: '#233841',
    height: 6,
    padding: '13px 0',
  },
  thumb: {
    height: 12,
    width: 12,
    backgroundColor: '#fff',
    marginTop: 0,
    marginLeft: -6,
    '&$active': {
      boxShadow: '0 0 0 6px rgba(35, 56, 65, 0.16) !important',
    },
    '&:focus, &:hover': {
      boxShadow: '0 0 0 4px rgba(35, 56, 65, 0.16)',
    },
    boxShadow: '0 0 0 4px rgba(35, 56, 65, 0.12)',
  },
  active: {},
  valueLabel: {
    top: -30,
    left: 'calc(-50% - 12px)',
    background: '#233841',
    padding: '0px 8px',
    borderRadius: 12,
    '& *': {
      background: 'transparent',
      color: 'white',
    },
  },
  track: {
    height: 6,
    borderRadius: 4,
    // Coloured by the segmented strip behind the rail, not here.
    backgroundColor: 'transparent',
  },
  rail: {
    height: 6,
    borderRadius: 4,
    backgroundColor: 'transparent',
    opacity: 1,
  },
})(Slider);

interface ThresholdSingleSliderProps {
  /** Current value. */
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Fill colour of the [min → value] portion of the track. */
  color: string;
  /** Colour of the remaining [value → max] portion. Defaults to a light rail. */
  trackColor?: string;
  /** Called (debounced) with the new value. */
  onChange: (value: number) => void;
  /** Appended to the value label / bounds (e.g. "%"). */
  suffix?: string;
}

function ThresholdSingleSliderBase({
  value,
  min,
  max,
  step = 1,
  color,
  trackColor = '#DDE3E7',
  onChange,
  suffix = '',
}: ThresholdSingleSliderProps) {
  const [internal, setInternal] = useState(value);

  useDebounce(
    () => {
      if (value !== internal) onChange(internal);
    },
    300,
    [internal],
  );

  useEffect(() => {
    if (value !== internal) setInternal(value);
  }, [value]);

  const span = max - min || 1;
  const pct = ((internal - min) / span) * 100;

  return (
    <div className="w-full">
      <div className="flex items-center gap-4 bg-v2blueGray-0 px-4 h-[44px] rounded-lg">
        <div className="relative flex-1">
          {/* Fill / rail strip behind the transparent MUI rail */}
          <div
            className="absolute left-0 right-0 flex h-[6px] rounded overflow-hidden pointer-events-none"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <div style={{ width: `${pct}%`, background: color }} />
            <div style={{ flex: 1, background: trackColor }} />
          </div>

          <PrettoSingleSlider
            value={internal}
            min={min}
            max={max}
            step={step}
            valueLabelDisplay="auto"
            valueLabelFormat={(v: number) => `${v}${suffix}`}
            onChange={(_e: React.ChangeEvent<unknown>, v: number | number[]) => {
              if (typeof v === 'number') setInternal(v);
            }}
            aria-labelledby="threshold-single-slider"
          />
        </div>
      </div>

      <div className="flex justify-between mt-1 px-4">
        <span className="text-[10px] text-v2blueGray-500">
          {min}
          {suffix}
        </span>
        <span className="text-[10px] text-v2blueGray-500">
          {max}
          {suffix}
        </span>
      </div>
    </div>
  );
}

export const ThresholdSingleSlider = memo(ThresholdSingleSliderBase);
export default ThresholdSingleSlider;
